import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RotateCw, ExternalLink, Image as ImageIcon, AlertCircle } from "lucide-react";
import { adminListOgSnapshots, adminTriggerOgRescrape } from "@/lib/admin-og.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/og-snapshots")({
  head: () => ({
    meta: [
      { title: "OG snapshots — Admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: OgSnapshotsPage,
});

type Snapshot = {
  id: string;
  url: string;
  fetched_at: string;
  status_code: number | null;
  title: string | null;
  description: string | null;
  og_title: string | null;
  og_description: string | null;
  og_image: string | null;
  twitter_card: string | null;
  twitter_image: string | null;
  canonical: string | null;
  error: string | null;
};

function OgSnapshotsPage() {
  const list = useServerFn(adminListOgSnapshots);
  const trigger = useServerFn(adminTriggerOgRescrape);
  const [running, setRunning] = useState(false);
  const q = useQuery({
    queryKey: ["admin-og-snapshots"],
    queryFn: () => list({ data: undefined as never }),
    refetchOnWindowFocus: false,
  });
  const snapshots = (q.data?.snapshots ?? []) as Snapshot[];

  // Latest snapshot per URL.
  const latestByUrl = useMemo(() => {
    const m = new Map<string, Snapshot>();
    for (const s of snapshots) if (!m.has(s.url)) m.set(s.url, s);
    return Array.from(m.values()).sort((a, b) => a.url.localeCompare(b.url));
  }, [snapshots]);

  // History per URL (already newest-first).
  const historyByUrl = useMemo(() => {
    const m = new Map<string, Snapshot[]>();
    for (const s of snapshots) {
      const arr = m.get(s.url) ?? [];
      arr.push(s);
      m.set(s.url, arr);
    }
    return m;
  }, [snapshots]);

  async function runNow() {
    setRunning(true);
    try {
      const r = await trigger({ data: {} });
      if (r.ok) {
        toast.success("Re-scrape complete");
        await q.refetch();
      } else {
        toast.error(`Re-scrape failed (${r.status})`);
      }
    } catch (e) {
      toast.error(`Failed: ${String(e)}`);
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-6xl px-4 py-10">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">OG / Twitter snapshots</h1>
            <p className="text-sm text-muted-foreground">
              Server-fetched share metadata for canonical URLs. Scheduled job runs in the
              background; you can also trigger a re-scrape now.
            </p>
          </div>
          <Button onClick={runNow} disabled={running}>
            <RotateCw className={`mr-2 h-4 w-4 ${running ? "animate-spin" : ""}`} />
            Re-scrape now
          </Button>
        </div>

        {q.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {q.error && <p className="text-sm text-destructive">Failed to load.</p>}
        {!q.isLoading && latestByUrl.length === 0 && (
          <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
            No snapshots yet. Click <strong>Re-scrape now</strong> to capture the first set.
          </div>
        )}

        <div className="grid gap-4">
          {latestByUrl.map((s) => {
            const history = historyByUrl.get(s.url) ?? [];
            return (
              <article key={s.id} className="rounded-lg border bg-card p-4">
                <header className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 font-medium hover:underline"
                    >
                      <span className="truncate">{s.url}</span>
                      <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                    </a>
                    <p className="text-xs text-muted-foreground">
                      Last fetched {new Date(s.fetched_at).toLocaleString()} · {history.length} snapshot(s)
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {s.status_code != null && (
                      <Badge variant={s.status_code >= 400 ? "destructive" : "secondary"}>
                        HTTP {s.status_code}
                      </Badge>
                    )}
                    {s.error && (
                      <Badge variant="destructive" className="gap-1">
                        <AlertCircle className="h-3 w-3" /> Error
                      </Badge>
                    )}
                  </div>
                </header>

                <div className="mt-3 grid gap-3 md:grid-cols-[160px_1fr]">
                  <div className="flex h-32 w-full items-center justify-center overflow-hidden rounded-md border bg-muted md:h-40">
                    {s.og_image ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={s.og_image}
                        alt="og:image"
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <ImageIcon className="h-8 w-8 text-muted-foreground" />
                    )}
                  </div>
                  <dl className="grid grid-cols-1 gap-1.5 text-sm">
                    <Field label="og:title" value={s.og_title ?? s.title} />
                    <Field label="og:description" value={s.og_description ?? s.description} />
                    <Field label="og:image" value={s.og_image} mono />
                    <Field label="twitter:card" value={s.twitter_card} />
                    <Field label="twitter:image" value={s.twitter_image} mono />
                    <Field label="canonical" value={s.canonical} mono />
                    {s.error && <Field label="error" value={s.error} mono />}
                  </dl>
                </div>

                {history.length > 1 && (
                  <details className="mt-3">
                    <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                      History ({history.length})
                    </summary>
                    <ul className="mt-2 space-y-1 text-xs">
                      {history.map((h) => (
                        <li key={h.id} className="flex items-center gap-2">
                          <span className="tabular-nums text-muted-foreground">
                            {new Date(h.fetched_at).toLocaleString()}
                          </span>
                          <span className="text-muted-foreground">·</span>
                          <span className="truncate">{h.og_image ?? h.twitter_image ?? "(no image)"}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </article>
            );
          })}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string | null; mono?: boolean }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-2">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className={`break-words ${mono ? "font-mono text-xs" : ""}`}>
        {value || <span className="text-muted-foreground">—</span>}
      </dd>
    </div>
  );
}
